import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, X, BookOpen, ClipboardList, Trophy } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Suivi = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('journal');
  const [reviews, setReviews] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalStats, setJournalStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [victories, setVictories] = useState([]);
  const [newVictory, setNewVictory] = useState('');
  const [narrative, setNarrative] = useState(null);

  // Form state
  const [success1, setSuccess1] = useState('');
  const [success2, setSuccess2] = useState('');
  const [success3, setSuccess3] = useState('');
  const [energy, setEnergy] = useState([5]);
  const [sleep, setSleep] = useState([5]);
  const [digestion, setDigestion] = useState([5]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [reviewsRes, journalRes, statsRes, victoriesRes, narrativeRes] = await Promise.all([
        axios.get(`${API_URL}/weekly-reviews`, { headers }),
        axios.get(`${API_URL}/journal`, { headers }),
        axios.get(`${API_URL}/journal/stats`, { headers }),
        axios.get(`${API_URL}/victories`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/bilans/narrative`, { headers }).catch(() => ({ data: null })),
      ]);
      setReviews(reviewsRes.data);
      setJournalEntries(journalRes.data);
      setJournalStats(statsRes.data);
      setVictories(victoriesRes.data);
      if (narrativeRes.data) setNarrative(narrativeRes.data);
    } catch (error) {
      console.error('Erreur chargement suivi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const successes = [success1, success2, success3].filter(s => s.trim());
    if (successes.length === 0) {
      toast.error('Ajoutez au moins une réussite');
      setSubmitting(false);
      return;
    }
    try {
      await axios.post(
        `${API_URL}/weekly-reviews`,
        { successes, energy: energy[0], sleep: sleep[0], digestion: digestion[0] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bilan enregistré !');
      setShowForm(false);
      setSuccess1(''); setSuccess2(''); setSuccess3('');
      setEnergy([5]); setSleep([5]); setDigestion([5]);
      fetchAll();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const addVictory = async () => {
    if (!newVictory.trim()) return;
    try {
      await axios.post(`${API_URL}/victories`, { text: newVictory }, { headers: { Authorization: `Bearer ${token}` } });
      setNewVictory('');
      toast.success('Victoire ajout\u00e9e !');
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  // Group journal entries by date
  const groupedJournal = journalEntries.reduce((acc, entry) => {
    const date = entry.created_at ? entry.created_at.split('T')[0] : 'inconnu';
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  const modeLabel = (mode) => mode === 'zen' ? 'Zen' : 'Mouvement';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="suivi-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-sage-100 to-sand-50 px-6 py-8">
        <h1 className="text-4xl font-serif font-light text-sage-700 mb-2" data-testid="suivi-title">
          Suivi
        </h1>
        <p className="text-sage-700/70 font-sans">Votre parcours au fil des jours</p>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4 pb-2">
        <div className="flex bg-sage-100 rounded-xl p-1" data-testid="suivi-tabs">
          <button
            onClick={() => setActiveTab('journal')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-sans font-medium transition-all ${
              activeTab === 'journal'
                ? 'bg-white text-sage-700 shadow-sm'
                : 'text-sage-500 hover:text-sage-600'
            }`}
            data-testid="tab-journal"
          >
            <BookOpen className="w-4 h-4" />
            Journal
          </button>
          <button
            onClick={() => setActiveTab('bilans')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-sans font-medium transition-all ${
              activeTab === 'bilans'
                ? 'bg-white text-sage-700 shadow-sm'
                : 'text-sage-500 hover:text-sage-600'
            }`}
            data-testid="tab-bilans"
          >
            <ClipboardList className="w-4 h-4" />
            Bilans
          </button>
          <button
            onClick={() => setActiveTab('victoires')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-sans font-medium transition-all ${
              activeTab === 'victoires'
                ? 'bg-white text-sage-700 shadow-sm'
                : 'text-sage-500 hover:text-sage-600'
            }`}
            data-testid="tab-victoires"
          >
            <Trophy className="w-4 h-4" />
            Victoires
          </button>
        </div>
      </div>

      {/* ========== TAB: JOURNAL ========== */}
      {activeTab === 'journal' && (
        <div className="px-6 py-4">
          {/* Stats summary */}
          {journalStats && journalStats.total_entries > 0 && (
            <div className="bg-white rounded-2xl border border-sand-200 p-5 mb-5" data-testid="journal-stats">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-sans font-medium text-sage-700">Vos ressentis</h3>
                <span className="text-xs text-sage-500 font-sans">
                  {journalStats.total_entries} entrée{journalStats.total_entries > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex flex-wrap gap-3">
                {journalStats.mood_distribution.map(([emoji, count]) => (
                  <div
                    key={emoji}
                    className="flex items-center gap-1.5 bg-sage-50 rounded-full px-3 py-1.5"
                  >
                    <span className="text-lg">{emoji}</span>
                    <span className="text-xs font-sans text-sage-600 font-medium">{count}</span>
                  </div>
                ))}
              </div>
              {journalStats.entries_this_week > 0 && (
                <p className="text-xs text-sage-500 font-sans mt-3">
                  {journalStats.entries_this_week} cette semaine
                </p>
              )}
            </div>
          )}

          {/* Journal entries grouped by date */}
          {Object.keys(groupedJournal).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(groupedJournal).map(([date, entries]) => (
                <div key={date}>
                  <p className="text-xs font-sans font-medium text-sage-500 uppercase tracking-wide mb-3">
                    {date !== 'inconnu'
                      ? format(new Date(date), 'EEEE d MMMM yyyy', { locale: fr })
                      : 'Date inconnue'}
                  </p>
                  <div className="space-y-3">
                    {entries.map((entry) => (
                      <div
                        key={entry.id}
                        className="bg-white rounded-xl border border-sand-200 p-4"
                        data-testid={`journal-entry-${entry.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-2xl flex-shrink-0 mt-0.5">{entry.mood}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-sans font-medium text-sage-700 truncate">
                                {entry.theme_slug?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Séance'}
                              </span>
                              <span className={`text-xs font-sans px-2 py-0.5 rounded-full ${
                                entry.mode === 'zen'
                                  ? 'bg-sage-100 text-sage-600'
                                  : 'bg-declic/10 text-declic'
                              }`}>
                                {modeLabel(entry.mode)}
                              </span>
                            </div>
                            {entry.note && (
                              <p className="text-sm text-sage-600 font-sans leading-relaxed">
                                {entry.note}
                              </p>
                            )}
                            <p className="text-xs text-sage-400 font-sans mt-1.5">
                              {entry.created_at
                                ? format(new Date(entry.created_at), 'HH:mm', { locale: fr })
                                : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <BookOpen className="w-10 h-10 text-sage-300 mx-auto mb-3" />
              <p className="text-sage-500 font-sans text-sm mb-1">Aucune entrée dans votre journal</p>
              <p className="text-sage-400 font-sans text-xs mb-4">
                Après chaque séance, notez votre ressenti
              </p>
              <button
                onClick={() => setActiveTab('bilans')}
                className="text-declic text-sm font-medium hover:underline"
                data-testid="journal-empty-action"
              >
                Commencer un bilan hebdo
              </button>
            </div>
          )}
        </div>
      )}

      {/* ========== TAB: BILANS HEBDO ========== */}
      {activeTab === 'bilans' && (
        <div>
          {/* Bilan narratif auto */}
          {narrative && (
            <div className="px-6 pt-4">
              <div className="bg-sage-50 rounded-xl p-4 border border-sage-100" data-testid="narrative-bilan">
                <p className="text-sage-700 text-sm font-medium mb-1">Cette semaine</p>
                <p className="text-sage-600 text-sm leading-relaxed">{narrative.narrative}</p>
              </div>
            </div>
          )}

          {/* Add button */}
          <div className="px-6 py-6">
            {!showForm && (
              <Button
                onClick={() => setShowForm(true)}
                className="w-full bg-sage-500 hover:bg-sage-600 text-white h-12 rounded-xl"
                data-testid="suivi-add-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Nouveau bilan hebdo
              </Button>
            )}

            {/* Form */}
            {showForm && (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-sand-200 p-6 space-y-6" data-testid="suivi-form">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-serif font-light text-sage-700">Nouveau bilan</h2>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowForm(false)}
                    data-testid="suivi-form-close"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-3">
                  <Label className="text-sage-700 font-sans font-medium">Mes 3 réussites cette semaine</Label>
                  <Textarea value={success1} onChange={(e) => setSuccess1(e.target.value)} placeholder="Réussite 1" className="border-sand-200 focus:border-sage-500" data-testid="suivi-success-1" />
                  <Textarea value={success2} onChange={(e) => setSuccess2(e.target.value)} placeholder="Réussite 2 (optionnel)" className="border-sand-200 focus:border-sage-500" data-testid="suivi-success-2" />
                  <Textarea value={success3} onChange={(e) => setSuccess3(e.target.value)} placeholder="Réussite 3 (optionnel)" className="border-sand-200 focus:border-sage-500" data-testid="suivi-success-3" />
                </div>

                <div className="space-y-6">
                  <div>
                    <Label className="text-sage-700 font-sans font-medium mb-3 block">Énergie : {energy[0]}/10</Label>
                    <Slider value={energy} onValueChange={setEnergy} min={1} max={10} step={1} className="w-full" data-testid="suivi-energy-slider" />
                  </div>
                  <div>
                    <Label className="text-sage-700 font-sans font-medium mb-3 block">Sommeil : {sleep[0]}/10</Label>
                    <Slider value={sleep} onValueChange={setSleep} min={1} max={10} step={1} className="w-full" data-testid="suivi-sleep-slider" />
                  </div>
                  <div>
                    <Label className="text-sage-700 font-sans font-medium mb-3 block">Digestion : {digestion[0]}/10</Label>
                    <Slider value={digestion} onValueChange={setDigestion} min={1} max={10} step={1} className="w-full" data-testid="suivi-digestion-slider" />
                  </div>
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-sage-500 hover:bg-sage-600 text-white h-12 rounded-xl" data-testid="suivi-submit-button">
                  {submitting ? 'Enregistrement...' : 'Enregistrer mon bilan'}
                </Button>
              </form>
            )}
          </div>

          {/* Reviews history */}
          {reviews.length > 0 && (
            <div className="px-6 py-4">
              <h2 className="text-lg font-sans font-medium text-sage-700 mb-4">Historique</h2>
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="bg-white rounded-xl border border-sand-200 p-5" data-testid={`review-card-${review.id}`}>
                    <p className="text-sm text-sage-700/60 font-sans mb-3">
                      Semaine du {format(new Date(review.week_start), 'dd MMMM yyyy', { locale: fr })}
                    </p>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-sans font-medium text-sage-700 mb-2">Réussites</p>
                        <ul className="list-disc list-inside space-y-1">
                          {review.successes.map((success, idx) => (
                            <li key={idx} className="text-sm text-sage-700">{success}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-sand-200">
                        <div>
                          <p className="text-xs text-sage-700/60 font-sans">Énergie</p>
                          <p className="text-lg font-sans font-medium text-sage-700">{review.energy}/10</p>
                        </div>
                        <div>
                          <p className="text-xs text-sage-700/60 font-sans">Sommeil</p>
                          <p className="text-lg font-sans font-medium text-sage-700">{review.sleep}/10</p>
                        </div>
                        <div>
                          <p className="text-xs text-sage-700/60 font-sans">Digestion</p>
                          <p className="text-lg font-sans font-medium text-sage-700">{review.digestion}/10</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {reviews.length === 0 && !showForm && (
            <div className="px-6 py-12 text-center">
              <ClipboardList className="w-10 h-10 text-sage-300 mx-auto mb-3" />
              <p className="text-sage-500 font-sans text-sm">Aucun bilan enregistré</p>
              <p className="text-sage-400 font-sans text-xs mt-1">
                Faites le point chaque semaine sur vos progrès
              </p>
            </div>
          )}
        </div>
      )}

      {/* ========== TAB: VICTOIRES ========== */}
      {activeTab === 'victoires' && (
        <div className="px-6 py-4">
          {/* Ajouter une victoire */}
          <div className="flex gap-2 mb-6" data-testid="victory-form">
            <input
              type="text"
              value={newVictory}
              onChange={(e) => setNewVictory(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addVictory()}
              placeholder="J'ai r&eacute;sist&eacute; &agrave; une envie..."
              className="flex-1 border border-sage-200 rounded-xl px-4 py-3 text-sm text-sage-700 placeholder:text-sage-400 focus:outline-none focus:border-declic"
              maxLength={200}
            />
            <Button
              onClick={addVictory}
              disabled={!newVictory.trim()}
              className="bg-declic hover:bg-declic/90 text-white rounded-xl px-4 h-auto"
              data-testid="add-victory-btn"
            >
              <Plus className="w-5 h-5" />
            </Button>
          </div>

          {/* Liste des victoires */}
          {victories.length > 0 ? (
            <div className="space-y-2">
              {victories.map((v, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-sage-100">
                  <Trophy className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sage-700 text-sm">{v.text}</p>
                    <p className="text-sage-400 text-xs mt-1">{v.date}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <Trophy className="w-10 h-10 text-sage-300 mx-auto mb-3" />
              <p className="text-sage-500 text-sm">Aucune victoire not&eacute;e</p>
              <p className="text-sage-400 text-xs mt-1">Chaque petite r&eacute;ussite compte</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Suivi;
